/**
 * The party call WRITE flows — v24 C1. Until this file the web surface was read-only:
 * calls were logged by API clients and the screens only rendered them. C1 gives the log
 * flow a home (with the serial field) and wires the amend path.
 *
 * ===========================================================================
 * TWO FORMS, ONE FIELD SET — because an amendment IS a complete statement
 * ===========================================================================
 * `LogCallForm` posts `POST /countries/:cc/party-calls`; `AmendCallForm` posts
 * `POST /parties/:id/calls/:callId/amend`. Both carry the same three fields (outcome,
 * note, serial) because an amendment is not a diff: it says what the record should have
 * said, IN FULL, and the original stays beside it byte-unchanged. The shared markup is
 * `CallFields`, so the two flows cannot drift in what they ask.
 *
 * ===========================================================================
 * THE SERIAL IS A CLAIM, AND THE HINT SAYS SO
 * ===========================================================================
 * `tally_serial` is captured UNVALIDATED by design until D-2 lands (AM-5) — a claim,
 * not a fact. The field's hint states that at the point of capture, so an operator
 * never mistakes the box for a licence lookup. BLANK MEANS ABSENT: a `tally_serial: ''`
 * would record that the question was asked and answered with nothing (the
 * `InquiryCreatePage` rule), so the wire body omits both optional fields when empty.
 *
 * ===========================================================================
 * WHO SEES THESE FORMS
 * ===========================================================================
 * The PANEL decides: write controls render only for a `parties.create` holder in the
 * row's country (`PartyCallHistoryPanel`), the same key that gates both routes at the
 * server. A form rendered to a seat the server would refuse is a promise the submit
 * breaks — the AssignPopover lesson.
 *
 * NO TOAST: these flows live on surfaces without a `ToastProvider` (the party-calls
 * routes mount none, `routes.tsx` says why) — success is the list refreshing with the
 * new row on top, failure is an inline Alert carrying the server's own sentence.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CountryCode } from '../../domain/countries';
import { Alert, Button, SelectField, TextAreaField, TextField } from '../../components/ui';
import {
  OUTCOME_LABEL,
  amendPartyCall,
  logPartyCall,
  partyCallErrorText,
} from './api';
import type { AmendPartyCallBody, PartyCallOutcome, PartyCallRow } from './api';

/**
 * The outcome choices: the EMPTY choice leads and maps to OMISSION on the wire, so the
 * server's explicit `'unspecified'` default stays the single spelling of "nobody said".
 * The five that assert something follow, worded by the shared label map.
 */
const OUTCOME_CHOICES: readonly { value: '' | PartyCallOutcome; label: string }[] = [
  { value: '', label: `${OUTCOME_LABEL.unspecified} (default)` },
  { value: 'no_answer', label: OUTCOME_LABEL.no_answer },
  { value: 'connected', label: OUTCOME_LABEL.connected },
  { value: 'callback_requested', label: OUTCOME_LABEL.callback_requested },
  { value: 'resolved', label: OUTCOME_LABEL.resolved },
  { value: 'escalated', label: OUTCOME_LABEL.escalated },
];

interface CallDraft {
  outcome: '' | PartyCallOutcome;
  note: string;
  tallySerial: string;
}

const EMPTY_DRAFT: CallDraft = { outcome: '', note: '', tallySerial: '' };

/** Draft → wire body. Blank optionals are OMITTED, never sent as `''`. */
function toBody(draft: CallDraft): AmendPartyCallBody {
  return {
    note: draft.note.trim(),
    ...(draft.outcome === '' ? {} : { outcome: draft.outcome }),
    ...(draft.tallySerial.trim() === '' ? {} : { tally_serial: draft.tallySerial.trim() }),
  };
}

function CallFields({
  draft,
  onChange,
  idPrefix,
}: {
  draft: CallDraft;
  onChange: (next: CallDraft) => void;
  idPrefix: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <TextAreaField
        label="What was said"
        rows={3}
        value={draft.note}
        onChange={(event) => onChange({ ...draft, note: event.target.value })}
        hint="Stays on the record in full — it never reaches the events feed."
      />
      <div className="flex flex-wrap gap-3">
        <div className="min-w-48 flex-1">
          <SelectField
            id={`${idPrefix}-outcome`}
            label="Outcome"
            value={draft.outcome}
            onChange={(value) => onChange({ ...draft, outcome: value as '' | PartyCallOutcome })}
            options={OUTCOME_CHOICES.map((choice) => ({
              value: choice.value,
              label: choice.label,
            }))}
          />
        </div>
        <div className="min-w-48 flex-1">
          <TextField
            label="Tally serial"
            value={draft.tallySerial}
            maxLength={20}
            onChange={(event) => onChange({ ...draft, tallySerial: event.target.value })}
            placeholder="As the customer stated it"
          />
          {/* AM-5, at the point of capture: a claim, not a fact. */}
          <p className="mt-1.5 text-label text-fg-muted">
            Recorded as said — not checked against the licence register.
          </p>
        </div>
      </div>
    </div>
  );
}

/** LOG A CALL — the create flow, mounted by the history panel for a write seat. */
export function LogCallForm({
  countryCode,
  partyId,
  onDone,
}: {
  countryCode: CountryCode;
  partyId: string;
  /** Called after a successful post AND on cancel — the parent owns the disclosure. */
  onDone: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<CallDraft>(EMPTY_DRAFT);
  const [blankNote, setBlankNote] = useState(false);

  const log = useMutation({
    mutationFn: (body: AmendPartyCallBody) =>
      logPartyCall(countryCode, { party_id: partyId, ...body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['party-calls'] });
      onDone();
    },
  });

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (draft.note.trim() === '') {
      // The server refuses a blank note with a 422; saying it here saves the round
      // trip without weakening anything — the server's floor still stands.
      setBlankNote(true);
      return;
    }
    setBlankNote(false);
    log.mutate(toBody(draft));
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" aria-label="Log a call">
      <CallFields draft={draft} onChange={setDraft} idPrefix="log-call" />
      {blankNote ? (
        <p className="text-label text-danger-fg">Write down what was said — a blank note is not a record.</p>
      ) : null}
      {log.isError ? (
        <Alert tone="danger" title="The call was not logged">
          {partyCallErrorText(log.error)}
        </Alert>
      ) : null}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={log.isPending}>
          {log.isPending ? 'Logging…' : 'Log the call'}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} disabled={log.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * AMEND A CALL — appends a CORRECTION row pointing at `row`. Offered only on rows that
 * are not themselves amendments (chains are flat; the server refuses what this screen
 * does not offer).
 */
export function AmendCallForm({
  row,
  onDone,
}: {
  row: PartyCallRow;
  onDone: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  // Prefilled from the row being corrected: an amendment restates the record in full,
  // and most corrections change one thing — retyping the rest would invite drift.
  const [draft, setDraft] = useState<CallDraft>({
    outcome: row.outcome === 'unspecified' ? '' : row.outcome,
    note: row.note,
    tallySerial: row.tally_serial ?? '',
  });
  const [blankNote, setBlankNote] = useState(false);

  const amend = useMutation({
    mutationFn: (body: AmendPartyCallBody) => amendPartyCall(row.party_id, row.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['party-calls'] });
      onDone();
    },
  });

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (draft.note.trim() === '') {
      setBlankNote(true);
      return;
    }
    setBlankNote(false);
    amend.mutate(toBody(draft));
  };

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3 rounded-md border border-line bg-subtle p-3"
      aria-label={`Amend call ${row.id}`}
    >
      <p className="text-body13 text-fg-secondary">
        This appends a correction that cites call {row.id}. The original entry stays on
        the record, unchanged.
      </p>
      <CallFields draft={draft} onChange={setDraft} idPrefix={`amend-call-${row.id}`} />
      {blankNote ? (
        <p className="text-label text-danger-fg">Write down what was said — a blank note is not a record.</p>
      ) : null}
      {amend.isError ? (
        <Alert tone="danger" title="The correction was not recorded">
          {partyCallErrorText(amend.error)}
        </Alert>
      ) : null}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={amend.isPending}>
          {amend.isPending ? 'Recording…' : 'Record the correction'}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} disabled={amend.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
